import { FetchArgs } from "@reduxjs/toolkit/query"
import { UserbotTracking } from "../interfaces/tracking"

export function scheduleRunsRequest(
  input: UserbotTracking.ScheduleRunsQuery,
): FetchArgs {
  const request = trackingResourceRequest("scheduled-chains", input, false)
  const params: Record<string, number> = {}
  for (const key of ["limit", "offset"] as const) {
    const value = input[key]
    if (value === undefined) continue
    if (
      !Number.isInteger(value) ||
      value < (key === "limit" ? 1 : 0) ||
      (key === "limit" && value > 500)
    )
      throw new Error("limit: 1–500, offset: целое ≥ 0")
    params[key] = value
  }
  return { ...request, url: `${request.url}/runs`, params }
}

export const trackingLists = {
  requests: {
    path: "join-requests",
    sorts: [
      "requestedAt",
      "expiresAt",
      "lastCheckedAt",
      "createdAt",
      "updatedAt",
    ],
    filters: {
      accountId: [],
      status: ["pending", "approved", "expired", "invalid", "cancelled"],
      reason: [],
    },
    time: "requestedAt",
  },
  recovery: {
    path: "join-requests/recovery",
    sorts: ["createdAt"],
    filters: { sourceClientId: [], reason: [] },
    time: "createdAt",
  },
  dialogs: {
    path: "dialogs",
    sorts: ["discoveredAt", "lastSeenAt", "createdAt", "updatedAt"],
    filters: {
      accountId: [],
      status: ["pending", "matched", "unmatched"],
      archiveStatus: ["pending", "archived", "failed", "unknown"],
    },
    time: "discoveredAt",
  },
  passes: {
    path: "dialogs/passes",
    sorts: ["createdAt", "updatedAt", "completedAt"],
    filters: {
      accountId: [],
      kind: ["collect", "match", "reconcile", "baseline"],
      status: ["running", "parked", "completed", "cancelled"],
    },
    time: "createdAt",
  },
  clarifications: {
    path: "dialogs/clarifications",
    sorts: ["expiresAt", "expectedObservedAt"],
    filters: {
      accountId: [],
      status: ["pending", "member", "left", "unknown", "inaccessible"],
    },
    time: "expectedObservedAt",
  },
} as const
export type TrackingListKind = keyof typeof trackingLists
export const isTrackingUuid = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)

const isIsoTime = (value: unknown) => {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
      value,
    ) ||
    !Number.isFinite(Date.parse(value))
  )
    return false
  // Date.parse normalizes February 30 rather than rejecting it.
  const day = value.slice(0, 10)
  return new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) === day
}

export function trackingListRequest(
  kind: TrackingListKind,
  input: object = {},
): FetchArgs {
  const config = trackingLists[kind]
  const source = input as Record<string, unknown>
  const params: Record<string, string | number> = {}
  for (const key of [
    "limit",
    "offset",
    "order",
    "sortBy",
    "from",
    "until",
    ...Object.keys(config.filters),
  ]) {
    const value = source[key]
    if (value === undefined || value === "") continue
    if (typeof value !== "string" && typeof value !== "number")
      throw new Error(`Некорректный ${key}`)
    params[key] = value
  }
  for (const key of ["limit", "offset"] as const) {
    const v = params[key]
    if (
      v !== undefined &&
      (!Number.isInteger(v) ||
        Number(v) < (key === "limit" ? 1 : 0) ||
        (key === "limit" && Number(v) > 500))
    )
      throw new Error("limit: 1–500, offset: целое ≥ 0")
  }
  for (const key of ["accountId", "sourceClientId"])
    if (params[key] !== undefined && !isTrackingUuid(params[key]))
      throw new Error(`${key}: требуется UUID`)
  if (
    params.reason !== undefined &&
    (typeof params.reason !== "string" ||
      !/^[a-z][a-z0-9-]{0,99}$/.test(params.reason))
  )
    throw new Error("reason: безопасный код (a-z, 0-9, дефис), до 100 символов")
  if (
    params.order !== undefined &&
    params.order !== "ASC" &&
    params.order !== "DESC"
  )
    throw new Error("order: ASC или DESC")
  if (
    params.sortBy !== undefined &&
    !(config.sorts as readonly unknown[]).includes(params.sortBy)
  )
    throw new Error("Недопустимое поле сортировки")
  for (const [key, values] of Object.entries(config.filters))
    if (
      values.length &&
      params[key] !== undefined &&
      !(values as readonly unknown[]).includes(params[key])
    )
      throw new Error(`Некорректный ${key}`)
  for (const key of ["from", "until"])
    if (params[key] !== undefined && !isIsoTime(params[key]))
      throw new Error(`${key}: требуется ISO время с часовым поясом`)
  if (
    params.from &&
    params.until &&
    Date.parse(String(params.from)) > Date.parse(String(params.until))
  )
    throw new Error("from должно быть ≤ until")
  return { url: config.path, method: "GET", params }
}

export function trackingResourceRequest(
  path: string,
  input: { id: string; accountId?: string },
  scoped: boolean,
  cancel = false,
): FetchArgs {
  if (!isTrackingUuid(input.id)) throw new Error("id: требуется UUID")
  if (scoped && !isTrackingUuid(input.accountId))
    throw new Error("accountId: требуется UUID")
  return {
    url: `${path}/${input.id}${cancel ? "/cancel" : ""}`,
    method: cancel ? "POST" : "GET",
    ...(scoped ? { params: { accountId: input.accountId } } : {}),
  }
}
